import type { ComponentType } from "react";
import type { Schema } from "@/shared/components/Form/types";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import {
  CreateCollectionTypeMutation,
  UpdateCollectionTypeMutation,
  CollectionTypeEditQuery,
  CollectionTypeShowQuery,
  type UpdateCollectionTypeInput,
} from "@/admin/entities/collectionTypes";
import { PlatformsAutocompleteQuery } from "@/admin/entities/platforms";
import type {
  CollectionTypeEditQueryType,
  CollectionTypeShowQueryType,
} from "@/types/graphql/admin.generated";

const DISPLAY_MODE_OPTIONS = [
  { label: "Дефолт платформы", value: "" },
  { label: "Горизонтальный (16:9)", value: "HORIZONTAL" },
  { label: "Вертикальный (2:3)", value: "VERTICAL" },
];

export const filterSchema: Schema = {
  platformId: {
    label: "Платформа",
    type: "autocomplete",
    query: PlatformsAutocompleteQuery,
    xs: 12,
    fullWidth: true,
    priority: "primary",
  },
  withDeleted: {
    label: "Показывать удалённые",
    type: "checkbox",
    priority: "secondary",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
};

// Поля формы (порт референсной схемы RightholderCollectionTypes:
// title + showInSearch + collectionDisplayMode; localizedTitles не
// портируем — ru-only)
const editFieldsSchema: Schema = {
  title: {
    required: true,
    label: "Название",
    type: "text",
    xs: 12,
    fullWidth: true,
  },
  showInSearch: {
    label: "Показывать вкладку типа в поиске",
    type: "checkbox",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  collectionDisplayMode: {
    label: "Режим превью коллекций",
    type: "select",
    xs: 12,
    md: 6,
    fullWidth: true,
    options: DISPLAY_MODE_OPTIONS,
  },
};

const createFieldsSchema: Schema = {
  platformId: {
    required: true,
    label: "Платформа",
    type: "autocomplete",
    query: PlatformsAutocompleteQuery,
    xs: 12,
    fullWidth: true,
  },
  ...editFieldsSchema,
};

export const createSchema: CreateSchemaDefinition = {
  main: {
    label: "Тип коллекции",
    createMutation: CreateCollectionTypeMutation,
    schema: createFieldsSchema,
  },
} as const;

export const editSchemaBase: EditSchemaDefinition = {
  details: {
    label: "Просмотр",
    type: "show" as const,
    getTitle: (data: unknown) => {
      const typed = data as CollectionTypeShowQueryType;
      return typed?.collectionType?.title ?? null;
    },
    showQuery: CollectionTypeShowQuery,
  },
  edit: {
    label: "Основные настройки",
    type: "form" as const,
    getTitle: (data: unknown) => {
      const typed = data as CollectionTypeShowQueryType;
      return typed?.collectionType?.title ?? null;
    },
    updateMutation: UpdateCollectionTypeMutation,
    editQuery: CollectionTypeEditQuery,
    schema: editFieldsSchema,
    getEditData: (data: unknown): UpdateCollectionTypeInput => {
      const { collectionType } = data as CollectionTypeEditQueryType;
      return {
        title: collectionType.title,
        showInSearch: collectionType.showInSearch,
        collectionDisplayMode: collectionType.collectionDisplayMode ?? null,
      };
    },
  },
} as const;

export function createEditSchema(
  showComponent: ComponentType<{ editData?: unknown; id?: string }>
): EditSchemaDefinition {
  return {
    ...editSchemaBase,
    details: {
      ...editSchemaBase.details,
      showComponent,
    },
  };
}
